import {Users} from "./Users";
import {VerticalTabs} from "../../components/ui/vertical-tabs";
import React, {useContext, useEffect, useState} from "react";
import {
    createRole,
    deleteRole,
    listPermissions,
    listRoles,
    Permission,
    Role,
    updateRole,
    PermissionDefinition,
    getAvailableResources
} from "../../clients/metoro/rbac";
import {Button} from "../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "../../components/ui/dialog";
import {Input} from "../../components/ui/input";
import {Label} from "../../components/ui/label";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {toast} from "../../components/ui/use-toast";
import {Checkbox} from "../../components/ui/checkbox";
import AuthContext from "../../contexts/JWTContext";
import {Textarea} from "../../components/ui/textarea";
import axios from "../../utility/customAxios";
import {DataTable} from "../../components/ui/datatable/datatable";
import {ColumnDef} from "@tanstack/react-table";
import {MoreVertical, Trash} from "lucide-react";
import {TooltipProvider} from "../../components/ui/tooltip";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";

interface User {
    uuid: string;
    name: string;
    email: string;
    isAdmin: boolean;
    status: string;
    roles?: Role[];
}

export function UsersAndPermissionsTab() {
    const tabData = [
        {
            title: "Users",
            content: <Users/>
        },
        {
            title: "Roles",
            content: <Roles/>
        }
    ]

    return (
        <div className="flex flex-col space-y-4 p-4">
            <TooltipProvider>
                <h2 className="text-xl font-bold">Users and Roles</h2>
                <div className={"text-textdark"}> You can create roles and assign them to users to manage permissions
                    for
                    resources.
                </div>
                <div className={"mt-4"}>
                    <VerticalTabs data={tabData}/>
                </div>
            </TooltipProvider>
        </div>
    )
}

function Roles() {
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editRole, setEditRole] = useState<Role | null>(null);
    const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const authContext = useContext(AuthContext);
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    const loadData = async () => {
        try {
            const [rolesData, permissionsData] = await Promise.all([
                listRoles(),
                listPermissions()
            ]);
            setRoles(rolesData);
            setPermissions(permissionsData);
            setIsLoading(false);
        } catch (error) {
            let errorMessage = "Failed to load data"
            // @ts-ignore
            if (error.response && error.response.status === 403) {
                // @ts-ignore
                errorMessage = error.response.data.error
            }
            toast({
                title: "Failed to Load Roles and Permissions",
                description: errorMessage,
                variant: "destructive"
            });
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (!authContext?.user?.email) return;
        axios.get("/api/v1/account/organization").then((response) => {
            const users = response.data.organizationMembers;
            const foundUser = users.find((user: any) => user.email === authContext?.user?.email);
            if (foundUser) {
                setCurrentUser(foundUser);
            }
        }).catch((e) => {
            console.error(e)
        })
    }, [authContext?.user?.email]);

    const handleCreateRole = async (name: string, description: string, permissions: PermissionDefinition[]) => {
        try {
            await createRole(name, description, permissions);
            await loadData();
            setCreateDialogOpen(false);
            toast({
                title: "Success",
                description: "Role created successfully"
            });
        } catch (error) {
            console.error('Error creating role:', error);
            let errorMessage = "Failed to create role";
            // @ts-ignore
            if (error.response && error.response.status === 403) {
                // @ts-ignore
                errorMessage = error.response.data.error;
            }
            toast({
                title: "Failed to Create Role",
                description: errorMessage,
                variant: "destructive"
            });
        }
    };

    const handleUpdateRole = async (uuid: string, name: string, description: string, permissions: PermissionDefinition[]) => {
        try {
            await updateRole(uuid, name, description, permissions);
            await loadData();
            toast({
                title: "Success",
                description: "Role updated successfully"
            });
            setEditRole(null);
        } catch (error) {
            console.error('Error updating role:', error);
            let errorMessage = "Failed to update role";
            // @ts-ignore
            if (error.response && error.response.status === 403) {
                // @ts-ignore
                errorMessage = error.response.data.error;
            }
            toast({
                title: "Failed to Update Role",
                description: errorMessage,
                variant: "destructive"
            });
        }
    };
    const handleDeleteRole = async (uuid: string) => {
        try {
            await deleteRole(uuid);
            await loadData();
            setRoleToDelete(null);
            toast({
                title: "Success",
                description: "Role deleted successfully"
            });
        } catch (error) {
            console.error('Error deleting role:', error);
            let errorMessage = "Failed to delete role";
            // @ts-ignore
            if (error.response && error.response.status === 403) {
                // @ts-ignore
                errorMessage = error.response.data.error;
            }
            toast({
                title: "Failed to Delete Role",
                description: errorMessage,
                variant: "destructive"
            });
        }
    };

    const columns: ColumnDef<Role>[] = [
        {
            accessorKey: "name",
            header: "Name",
        },
        {
            accessorKey: "description",
            header: "Description",
        },
        {
            id: "permissions",
            header: "Permissions",
            cell: ({row}) => {
                const rolePermissions = permissions.filter(p =>
                    row.original.permissions?.includes(p.uuid)
                );

                const stylePermissions = (perm: string) => {
                    let parts = perm.split('/')
                    return parts.length > 0 ? parts[0] : parts
                }

                return (
                    <div className="max-w-[400px] space-y-1">
                        {rolePermissions.length > 0 ? (
                            rolePermissions.map((permission) => (
                                <div key={permission.uuid} className="text-sm">
                                    <span className="font-medium">{stylePermissions(permission.resource)}</span>
                                    <span className="text-textdark ml-2">
                                        ({permission.operation})
                                    </span>
                                </div>
                            ))
                        ) : (
                            <span className="text-sm text-textdark">No permissions assigned</span>
                        )}
                    </div>
                );
            },
        },
        {
            id: "actions",
            cell: ({row}) => {
                const role = row.original;
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4"/>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className={"bg-backgrounddark text-textmedium"}>
                            <DropdownMenuItem
                                className={"hover:cursor-pointer hover:bg-primarytransparent hover:border hover:border-primary rounded"}
                                onClick={() => setEditRole(role)}
                                requiredPermission={{
                                        resource: "accessManagement/default/*",
                                        operation: "update"
                                    }}
                                disabled={role.isMetoroManaged ? true : undefined}
                            >
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className={"hover:cursor-pointer hover:bg-red-500/10 hover:border hover:border-red-500 text-red-500 rounded"}
                                onClick={() => setRoleToDelete(role)}
                                requiredPermission={{
                                        resource: "accessManagement/default/*",
                                        operation: "delete"
                                }}
                                disabled={role.isMetoroManaged ? true : undefined}
                            >
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
        },
    ];

    return (
        <div className="space-y-4 flex flex-col h-full">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Roles</h3>
                <Button
                    requiredPermission={{
                        resource: "accessManagement/default/*",
                        operation: "create"
                    }}
                    onClick={() => setCreateDialogOpen(true)}
                    className={"border rounded border-primary bg-primarytransparent text-textmedium"}
                >
                    Create Role
                </Button>
            </div>

            <div className={"w-full h-full"}>
            <DataTable
                forcePagination={true}
                columns={columns}
                data={roles}
                isLoading={isLoading}
                filters={[]}
                textFilteringColumnName="name"
                textFilteringPlaceholder="Filter roles..."
            />
            </div>

            <RoleDialog
                open={createDialogOpen}
                onOpenChange={setCreateDialogOpen}
                onSubmit={handleCreateRole}
                permissions={permissions}
            />

            <RoleDialog
                open={!!editRole}
                onOpenChange={(open) => !open && setEditRole(null)}
                onSubmit={(name, description, permissions) => editRole && handleUpdateRole(editRole.uuid, name, description, permissions)}
                defaultValues={editRole}
                permissions={permissions}
            />

            <Dialog open={!!roleToDelete} onOpenChange={(open) => !open && setRoleToDelete(null)}>
                <DialogContent className="w-1/3">
                    <div className="flex flex-col gap-4 text-textmedium">
                        <h3 className="text-lg font-bold">Delete Role</h3>
                        <div className={"text-textmedium"}>Are you sure you want to delete this role? Users who are assigned to this role will lose access to the permissions granted by this role.</div>
                        {roleToDelete && (
                            <div className="text-textlight font-semibold bg-backgroundlight px-2 py-1 rounded w-full">
                                {roleToDelete.name}
                            </div>
                        )}
                        <div className="text-sm text-red-500">
                            Warning: This action cannot be undone. Users will need to be reassigned to different roles if they need to maintain similar permissions.
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button 
                                className="text-textmedium bg-red-500/10 border border-red-500 rounded"
                                onClick={() => setRoleToDelete(null)}
                            >
                                Cancel
                            </Button>
                            <Button 
                                className="text-textmedium bg-primarytransparent border border-primary rounded"
                                onClick={() => roleToDelete && handleDeleteRole(roleToDelete.uuid)}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

interface RoleDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (name: string, description: string, permissions: PermissionDefinition[]) => void;
    defaultValues?: {
        name: string;
        description?: string;
        permissions?: string[];
    } | null;
    permissions: Permission[];
}

interface PermissionPolicy {
    resource: string;
    operations: {
        create: boolean;
        read: boolean;
        update: boolean;
        delete: boolean;
    };
}

interface ResourceDefinition {
    displayValue: string;
    value: string;
}

const RoleDialog: React.FC<RoleDialogProps> = ({open, onOpenChange, onSubmit, defaultValues, permissions}) => {
    const [roleDetails, setRoleDetails] = useState({
        name: '',
        description: ''
    });
    const [permissionPolicies, setPermissionPolicies] = useState<PermissionPolicy[]>([]);
    const [availableResources, setAvailableResources] = useState<ResourceDefinition[]>([]);

    useEffect(() => {
        // Load available resources when dialog opens
        if (open) {
            getAvailableResources().then(resources => {
                setAvailableResources(resources);
            }).catch(error => {
                console.error('Error loading available resources:', error);
                let errorMessage = "Failed to load available resources";
                // @ts-ignore
                if (error.response && error.response.status === 403) {
                    // @ts-ignore
                    errorMessage = error.response.data.error;
                }
                toast({
                    title: "Failed to Load Resources",
                    description: errorMessage,
                    variant: "destructive"
                });
            });
        }
    }, [open]);

    useEffect(() => {
        if (defaultValues) {
            setRoleDetails({
                name: defaultValues.name,
                description: defaultValues.description || ''
            });
            // Convert existing permissions to policies
            const policies = defaultValues.permissions?.reduce<PermissionPolicy[]>((acc, permissionUUID) => {
                const permission = permissions.find(p => p.uuid === permissionUUID);
                if (permission) {
                    const operations = permission.operation.split(',');
                    acc.push({
                        resource: permission.resource,
                        operations: {
                            create: operations.includes('create'),
                            read: operations.includes('read'),
                            update: operations.includes('update'),
                            delete: operations.includes('delete')
                        }
                    });
                }
                return acc;
            }, []) || [];
            setPermissionPolicies(policies);
        } else {
            setRoleDetails({name: '', description: ''});
            setPermissionPolicies([]);
        }
    }, [defaultValues, permissions]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Convert permission policies to PermissionDefinition array
        const permissionDefs: PermissionDefinition[] = permissionPolicies
            .map(policy => {
                const operations = Object.entries(policy.operations)
                    .filter(([_, enabled]) => enabled)
                    .map(([op]) => op)
                    .join(',');
                
                if (operations && policy.resource) {
                    return {
                        resource: policy.resource,
                        operation: operations
                    };
                }
                return null;
            })
            .filter((def): def is PermissionDefinition => def !== null);

        if (permissionDefs.length === 0) {
            toast({
                title: "Error",
                description: "At least one permission with operations is required",
                variant: "destructive"
            });
            return;
        }

        onSubmit(roleDetails.name, roleDetails.description, permissionDefs);
        onOpenChange(false);
    };

    const addPolicy = () => {
        setPermissionPolicies([...permissionPolicies, {
            resource: '',
            operations: {
                create: false,
                read: false,
                update: false,
                delete: false
            }
        }]);
    };

    const removePolicy = (index: number) => {
        setPermissionPolicies(permissionPolicies.filter((_, i) => i !== index));
    };

    const updatePolicyResource = (index: number, resource: string) => {
        const newPolicies = [...permissionPolicies];
        newPolicies[index] = {...newPolicies[index], resource};
        setPermissionPolicies(newPolicies);
    };

    const updatePolicyOperation = (index: number, operation: keyof PermissionPolicy['operations'], value: boolean) => {
        const newPolicies = [...permissionPolicies];
        newPolicies[index] = {
            ...newPolicies[index],
            operations: {
                ...newPolicies[index].operations,
                [operation]: value
            }
        };
        setPermissionPolicies(newPolicies);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle className="text-xl">
                        {defaultValues ? 'Edit role' : 'Create role'}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-6 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name" className="text-sm text-textmedium">Name</Label>
                            <Input
                                id="name"
                                value={roleDetails.name}
                                onChange={(e) => setRoleDetails({...roleDetails, name: e.target.value})}
                                placeholder="Role name"
                                className="w-full rounded"
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="description" className="text-sm text-textmedium">Description</Label>
                            <Textarea
                                id="description"
                                value={roleDetails.description}
                                onChange={(e) => setRoleDetails({...roleDetails, description: e.target.value})}
                                placeholder="Role description"
                                className="w-full rounded border border-border text-textmedium"
                            />
                        </div>

                        <div className="space-y-4 text-textmedium">
                            <Label className="text-sm text-textmedium">Permissions</Label>
                            <div className={"max-h-[500px] flex flex-col overflow-y-auto gap-4"}>
                                {permissionPolicies.map((policy, index) => (
                                    <div key={index}
                                         className="flex items-center gap-4 p-4 border rounded bg-backgroundmedium">
                                        <div className="flex-1">
                                            <Select
                                                value={policy.resource}
                                                onValueChange={(value) => updatePolicyResource(index, value)}
                                            >
                                                <SelectTrigger
                                                    className="w-full border border-border rounded bg-backgrounddark">
                                                    <SelectValue placeholder="Select a resource"/>
                                                </SelectTrigger>
                                                <SelectContent className={"bg-background rounded text-textmedium"}>
                                                    {availableResources.map((resource) => (
                                                        <SelectItem
                                                            className={"hover:bg-primarytransparent hover:border hover:border-primary rounded hover:cursor-pointer"}
                                                            key={resource.value}
                                                            value={resource.value}>
                                                            {resource.displayValue}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex-1 grid grid-cols-2 gap-2">
                                            {Object.entries(policy.operations).map(([operation, enabled]) => (
                                                <div key={operation} className="flex items-center space-x-2">
                                                    <Checkbox
                                                        id={`${index}-${operation}`}
                                                        checked={enabled}
                                                        onCheckedChange={(checked) =>
                                                            updatePolicyOperation(index, operation as keyof PermissionPolicy['operations'], checked as boolean)
                                                        }
                                                    />
                                                    <Label
                                                        htmlFor={`${index}-${operation}`}
                                                        className="text-sm font-normal capitalize"
                                                    >
                                                        {operation}
                                                    </Label>
                                                </div>
                                            ))}
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removePolicy(index)}
                                            className="text-red-500"
                                        >
                                            <Trash className="h-4 w-4"/>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <Button
                            type="button"
                            onClick={addPolicy}
                            className="mt-2 w-full border border-primary bg-primarytransparent text-textmedium rounded"
                        >
                            Add permission
                        </Button>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button
                            type="submit"
                            disabled={!roleDetails.name.trim() || permissionPolicies.length === 0}
                            className="border rounded border-primary text-textmedium bg-primarytransparent"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
